import { authedFetch } from "./supabase";

const WEB_PUSH_PUBLIC_KEY =
  import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY ||
  "BD4Lt635-7ZsdnlI8jWdkpHyShmzuUzb6kC-cyGYZHP2TO4recD9AultdbThKgnceGEk26p_gPHedXBDyE6y40I";

export type PushState =
  | "unsupported"
  | "denied"
  | "default"
  | "enabled"
  | "disabled";

export const PUSH_STATE_CHANGED_EVENT = "hp-push-state-changed";

function emitPushStateChanged() {
  window.dispatchEvent(new Event(PUSH_STATE_CHANGED_EVENT));
}

function urlBase64ToArrayBuffer(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) output[i] = rawData.charCodeAt(i);
  return output.buffer as ArrayBuffer;
}

export function supportsPush(): boolean {
  return "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window &&
    window.isSecureContext;
}

async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return await navigator.serviceWorker.ready;
}

export async function getPushState(): Promise<PushState> {
  if (!supportsPush()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const registration = await serviceWorkerRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) return "enabled";
  return Notification.permission === "granted" ? "disabled" : "default";
}

export async function enableAdminPush(): Promise<PushState> {
  if (!supportsPush()) return "unsupported";

  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();
  if (permission === "denied") return "denied";
  if (permission !== "granted") return "default";

  const registration = await serviceWorkerRegistration();
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToArrayBuffer(WEB_PUSH_PUBLIC_KEY),
  });

  const res = await authedFetch("/admin-push-subscribe", {
    method: "POST",
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      user_agent: navigator.userAgent,
    }),
  });
  if (!res.ok) {
    await subscription.unsubscribe().catch(() => undefined);
    throw new Error(`Push opslaan mislukt: ${res.status} ${await res.text()}`);
  }

  emitPushStateChanged();
  return "enabled";
}

export async function disableAdminPush(): Promise<PushState> {
  if (!supportsPush()) return "unsupported";
  const registration = await serviceWorkerRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return "disabled";

  await authedFetch("/admin-push-subscribe", {
    method: "DELETE",
    body: JSON.stringify({ endpoint: subscription.endpoint }),
  }).catch(() => undefined);
  await subscription.unsubscribe();
  emitPushStateChanged();
  return "disabled";
}
